/**
 * AgentConnectorBrowser — the agent's "Add connector" (operator decision
 * 2026-10-21, option A): inside the Connectors tab, the tab turns into the
 * organization's list with a search — every entry with its description, its
 * type, and what adding it asks; the ones already on this agent say "Added".
 * Nothing is chosen for the person: each entry has its own Add (the old
 * dropdown pre-selected the first entry, so one click added a connector
 * nobody chose). An entry that needs this agent's own key asks for it in
 * place. "Not in the list?" opens the custom MCP / API forms.
 *
 * What adding does is unchanged: the connector follows the organization's
 * settings until someone edits it on the agent; a name clash offers the
 * suggested name, which makes it customized.
 */

import { useMemo, useState } from 'react';
import { useLanguage } from '@hooks/useLanguage';
import { showNotification } from '@/utils/showNotification';
import type { ApiConnector } from '@modules/chatbots/services/apiConnectorService';
import type { McpConnector } from '@modules/chatbots/services/mcpConnectorService';
import {
  connectorLibraryService,
  errorBody,
  type LibraryCredential,
  type LibraryKind,
  type PickerEntry,
} from '../services/connectorLibraryService';
import type { AgentConnectorLibrary } from '../hooks/useAgentConnectorLibrary';
import { addStep, browse } from '../utils/agentBrowse';
import { CredentialFields, KindTag, ServiceIcon, credentialComplete } from './LibraryBits';

interface Props {
  chatbotId: string;
  library: AgentConnectorLibrary;
  kinds: LibraryKind[];
  onBack: () => void;
  /** A connector was added — the tab reloads its lists. */
  onAdded: (kind: LibraryKind, connector: McpConnector | ApiConnector) => void;
  onCustom: (kind: LibraryKind) => void;
}

type Asking = { entryId: string; credential: LibraryCredential; clash?: { slug: string; suggested: string }; error?: string };

export function AgentConnectorBrowser({ chatbotId, library, kinds, onBack, onAdded, onCustom }: Props) {
  const { t, direction } = useLanguage();
  const [query, setQuery] = useState('');
  const [asking, setAsking] = useState<Asking | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const entries = useMemo(() => browse(library.entries, query, kinds), [library.entries, query, kinds]);

  const add = async (entry: PickerEntry, credential?: LibraryCredential, slug?: string) => {
    setBusyId(entry.entry_id);
    try {
      const { kind, connector } = await connectorLibraryService.addToAgent(chatbotId, entry.entry_id, { credential, slug });
      showNotification(t('connectorLibrary.addFrom.added', { name: entry.name }), 'success');
      setAsking(null);
      void library.reload();
      onAdded(kind, connector);
    } catch (err) {
      const body = errorBody(err);
      const next: Asking = { entryId: entry.entry_id, credential: credential ?? {} };
      if (body.field === 'slug' && body.suggested) {
        next.clash = { slug: body.slug ?? entry.slug, suggested: body.suggested };
      } else if (body.field === 'entry_id') {
        next.error = t('connectorLibrary.addFrom.alreadyOnAgent');
        void library.reload();
      } else {
        next.error = body.error || t('connectorLibrary.addFrom.error');
      }
      setAsking(next);
    } finally {
      setBusyId(null);
    }
  };

  const start = (entry: PickerEntry) => {
    if (addStep(entry) === 'token') setAsking({ entryId: entry.entry_id, credential: {} });
    else void add(entry);
  };

  const backArrow = direction === 'rtl' ? '→' : '←';

  return (
    <div className="cxb" data-testid="agent-connector-browser">
      <button type="button" className="cxb__back" onClick={onBack} data-testid="agent-connector-browser-back">
        <span aria-hidden="true">{backArrow}</span> {t('bots.connectors.title')}
      </button>
      <div className="cxb__head">
        <h3 className="cxb__title">{t('connectorLibrary.browse.title')}</h3>
        <p className="cxb__subtitle">{t('connectorLibrary.browse.fromList')}</p>
      </div>

      {library.failed ? (
        <div className="cxb__failed" role="alert" data-testid="agent-connector-browser-error">
          <span>{t('connectorLibrary.errors.loadList')}</span>
          <button type="button" className="cx-btn cx-btn--sm" onClick={() => void library.reload()}>{t('common.retry')}</button>
        </div>
      ) : library.entries.length > 0 && (
        <>
          <input className="form-input cxb__search" type="search" value={query} onChange={e => setQuery(e.target.value)}
                 placeholder={t('connectorLibrary.search')} aria-label={t('connectorLibrary.search')} dir="auto"
                 autoFocus data-testid="agent-connector-browser-search" />
          {entries.length === 0 ? (
            <p className="cxb__empty">{t('connectorLibrary.noMatch')}</p>
          ) : (
            <ul className="cxb__list" data-testid="agent-connector-browser-list">
              {entries.map(entry => {
                const step = addStep(entry);
                const open = asking?.entryId === entry.entry_id ? asking : null;
                const busy = busyId === entry.entry_id;
                return (
                  <li key={entry.entry_id} className={`cxb__row ${step === 'added' ? 'is-added' : ''}`}
                      data-testid={`agent-connector-browser-row-${entry.slug}`}>
                    <div className="cxb__main">
                      <ServiceIcon name={entry.name} slug={entry.slug} url={entry.kind === 'mcp' ? entry.url : entry.base_url} />
                      <span className="cxb__text">
                        <span className="cxb__name-line">
                          <span className="cxb__name" dir="auto">{entry.name}</span>
                          <KindTag kind={entry.kind} />
                        </span>
                        <span className="cxb__desc" dir="auto">
                          {entry.description || (entry.kind === 'mcp' ? entry.url : entry.base_url)}
                          {step === 'token' && <span className="cxb__needs"> · {t('connectorLibrary.browse.needsToken')}</span>}
                        </span>
                      </span>
                      {step === 'added' ? (
                        <span className="cx-pill" data-testid={`agent-connector-browser-added-${entry.slug}`}>
                          {t('connectorLibrary.browse.added')}
                        </span>
                      ) : !open && (
                        <button type="button" className="cx-btn cx-btn--sm" disabled={busy || !!busyId}
                                onClick={() => start(entry)} data-testid={`agent-connector-browser-add-${entry.slug}`}>
                          {busy ? t('common.loading') : t('connectorLibrary.browse.add')}
                        </button>
                      )}
                    </div>
                    {open && (
                      <div className="cxb__ask" data-testid={`agent-connector-browser-ask-${entry.slug}`}>
                        {entry.needs_credential && (
                          <>
                            <p className="cxb__ask-hint">{t('connectorLibrary.addFrom.credentialHint')}</p>
                            <CredentialFields entry={entry} value={open.credential}
                                              onChange={credential => setAsking({ ...open, credential })} />
                          </>
                        )}
                        {open.clash && (
                          <p className="cxb__ask-hint" role="alert">
                            {t('connectorLibrary.addFrom.slugTaken', { slug: open.clash.slug })} {t('connectorLibrary.addFrom.slugHint')}
                          </p>
                        )}
                        {open.error && <p className="cxb__ask-error" role="alert">{open.error}</p>}
                        <div className="cxb__ask-actions">
                          <button type="button" className="cx-btn cx-btn--sm" disabled={busy} onClick={() => setAsking(null)}>
                            {t('common.cancel')}
                          </button>
                          <button type="button" className="cx-btn cx-btn--sm cx-btn--primary"
                                  disabled={busy || (entry.needs_credential && !credentialComplete(entry, open.credential))}
                                  onClick={() => { void add(entry, entry.needs_credential ? open.credential : undefined, open.clash?.suggested); }}
                                  data-testid={`agent-connector-browser-confirm-${entry.slug}`}>
                            {open.clash ? t('connectorLibrary.addFrom.addAs', { slug: open.clash.suggested }) : t('connectorLibrary.browse.add')}
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      <p className="cxb__custom" data-testid="agent-connector-browser-custom">
        {library.entries.length > 0 || library.failed ? t('connectorLibrary.browse.notInList') : t('connectorLibrary.browse.noList')}{' '}
        {kinds.includes('mcp') && (
          <button type="button" className="cxb__custom-link" onClick={() => onCustom('mcp')} data-testid="agent-connector-custom-mcp">
            {t('connectorLibrary.browse.customMcp')}
          </button>
        )}
        {kinds.includes('mcp') && kinds.includes('api') && <span aria-hidden="true"> · </span>}
        {kinds.includes('api') && (
          <button type="button" className="cxb__custom-link" onClick={() => onCustom('api')} data-testid="agent-connector-custom-api">
            {t('connectorLibrary.browse.customApi')}
          </button>
        )}
      </p>
    </div>
  );
}
